import React, { useEffect, useRef, useState, useMemo } from 'react';
import './VistaCarpetaGenerica.css';
import FilePreviewModal from '../FilePreviewModal/FilePreviewModal';
import { useGestorArchivos } from '../../hooks/useGestorArchivos';
import UploadZone from '../ZonaDeSubida/ZonaDeSubida';
import api from '../../api/axiosConfig';
import { detectarTabKey, FOLDER_ROUTES } from '../../config/folderRoutes';

const SORT_OPTIONS = [
  { value: 'default', label: 'Por defecto' },
  { value: 'fecha-desc', label: 'Más reciente' },
  { value: 'nombre-asc', label: 'Nombre A - Z' },
  { value: 'nombre-desc', label: 'Nombre Z - A' },
  { value: 'tipo', label: 'Carpetas primero' },
];

const SORT_STORAGE_KEY = 'vistaCarpeta_sortOrder';

const n = (name) => name?.replace(/^\d{2}\s*-\s*/, '') || name;

const getFileIcon = (filename) => {
  if (!filename) return 'bi bi-file-earmark';
  const parts = filename.split('.');
  if (parts.length === 1) return 'bi bi-file-earmark';
  const extension = parts.pop().toLowerCase();
  switch (extension) {
    case 'pdf': return 'bi bi-filetype-pdf';
    case 'doc':
    case 'docx': return 'bi bi-filetype-docx';
    case 'xls':
    case 'xlsx':
    case 'csv': return 'bi bi-filetype-xlsx';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif': return 'bi bi-filetype-jpg';
    case 'zip':
    case 'rar': return 'bi bi-file-zip';
    default: return 'bi bi-file-earmark';
  }
};

const getFileIconColor = (filename) => {
  if (!filename) return '#718096';
  const parts = filename.split('.');
  if (parts.length === 1) return '#718096';
  const extension = parts.pop().toLowerCase();
  switch (extension) {
    case 'pdf': return '#e53e3e';
    case 'doc':
    case 'docx': return '#2b6cb0';
    case 'xls':
    case 'xlsx':
    case 'csv': return '#38a169';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif': return '#805ad5';
    case 'zip':
    case 'rar': return '#d69e2e';
    default: return '#718096';
  }
};

const VistaCarpetaGenerica = ( { crearNotificacion, notificaciones, carpetaId, nombreCliente, isAdmin, puedeSubir, startPath, titulo, entidadCuit, entidadId, addToast, targetFolderId, targetFolderPath, carpetasConNovedades, tabKey } ) => {
  const handleNotificarCliente = async (item) => {
    if (!isAdmin || !carpetaActual) return;
    console.log('Notificar - entidadCuit:', entidadCuit, 'entidadId:', entidadId);
    if (!entidadCuit && !entidadId) {
      if (addToast) addToast('No se puede notificar: selecciona una entidad.', 'warning');
      return;
    }
    try {
      const folderPath = [...history, { id: carpetaActual.id, nombre: carpetaActual.nombre }];
      const effectiveTabKey = tabKey || detectarTabKey(folderPath) || Object.keys(FOLDER_ROUTES).find(k => FOLDER_ROUTES[k].titulo === titulo);
      const body = {
        ...(entidadId ? { entidadId } : { cuitUser: entidadCuit }),
        archivoNombre: item.nombre,
        carpetaNombre: titulo || n(carpetaActual.nombre),
        carpetaRealId: carpetaActual.id,
        tabKey: effectiveTabKey,
        folderPath
      };
      await api.post('/notificarArchivoNuevo', body);
      if (addToast) addToast(`Notificado: ${item.nombre}`, 'success');
    } catch (e) {
      console.error('Error al notificar:', e, e?.response?.data);
      if (addToast) addToast(e?.response?.data?.error || 'Error al notificar.', 'warning');
    }
  };

  const {
    history,
    carpetaActual,
    loading,
    error,
    archivoAEliminar,
    previewModalOpen,
    setPreviewModalOpen,
    previewUrl,
    previewFile,
    contenidoMixto,
    elementosOcultos,
    
    abrirCarpeta,
    refrescarCarpetaActual,
    handleVolver,
    handleItemClick,
    handleSubirArchivo,
    handleEliminarClick,
    confirmarEliminacion,
    cancelarEliminacion,
    handleDescargarArchivo,
    handleToggleVisibilidad
  } = useGestorArchivos({
    carpetaId, 
    startPath, 
    titulo, 
    nombreCliente, 
    crearNotificacion, 
    notificaciones,
    entidadCuit,
    addToast
  });

  const targetSeenRef = useRef(null);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem(SORT_STORAGE_KEY) || 'default';
  });

  const [refreshing, setRefreshing] = useState(false);

  const handleSortChange = (e) => {
    const newOrder = e.target.value;
    setSortOrder(newOrder);
    localStorage.setItem(SORT_STORAGE_KEY, newOrder);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refrescarCarpetaActual();
    } finally {
      setRefreshing(false);
    }
  };

  const parseFecha = (fechaStr) => {
    if (!fechaStr) return 0;
    const [datePart, timePart] = fechaStr.split(' ');
    const dateParts = datePart.split('/');
    const timeParts = timePart ? timePart.split(':') : [];
    if (dateParts.length === 3) {
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      let year = parseInt(dateParts[2], 10);
      if (year < 100) year += 2000;
      const hours = parseInt(timeParts[0] || '0', 10);
      const minutes = parseInt(timeParts[1] || '0', 10);
      return new Date(year, month, day, hours, minutes).getTime();
    }
    return 0;
  };

  const sortedContent = useMemo(() => {
    if (!contenidoMixto || !sortOrder || sortOrder === 'default') return contenidoMixto;
    const items = [...contenidoMixto];
    switch (sortOrder) {
      case 'fecha-desc':
        return items.sort((a, b) => parseFecha(b.fecha) - parseFecha(a.fecha));
      case 'nombre-asc':
        return items.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
      case 'nombre-desc':
        return items.sort((a, b) => b.nombre.localeCompare(a.nombre, 'es'));
      case 'tipo':
        return items.sort((a, b) => {
          if (a.tipo === 'Carpeta' && b.tipo !== 'Carpeta') return -1;
          if (a.tipo !== 'Carpeta' && b.tipo === 'Carpeta') return 1;
          return a.nombre.localeCompare(b.nombre, 'es');
        });
      default:
        return items;
    }
  }, [contenidoMixto, sortOrder]);

  useEffect(() => {
    console.log('[AUTO-NAV] carpetaActual:', carpetaActual?.id?.slice(0, 15), 'targetFolderId:', targetFolderId?.slice(0, 15), 'seen:', targetSeenRef.current?.slice(0, 15));
    if (carpetaActual && targetFolderId && targetSeenRef.current !== targetFolderId) {
      targetSeenRef.current = targetFolderId;
      if (carpetaActual.id !== targetFolderId) {
        console.log('[AUTO-NAV] NAVEGANDO a:', targetFolderId.slice(0, 15));
        abrirCarpeta(targetFolderId, '(sin nombre)', false);
      }
    }
  }, [carpetaActual, targetFolderId, abrirCarpeta]);

  useEffect(() => {
    if (carpetaActual && carpetasConNovedades && carpetasConNovedades.includes(carpetaActual.id)) {
      api.post('/marcarCarpetaLeida', { carpetaId: carpetaActual.id }).catch(() => {});
    }
  }, [carpetaActual?.id, carpetasConNovedades]);

  const SkeletonLoader = () => (
    <div className="skeleton-container">
      {[1, 2, 3].map((i) => (
        <div key={i} className="skeleton-item">
          <div className="skeleton-left">
            <div className="skeleton-icon"></div>
            <div className="skeleton-text-container">
              <div className="skeleton-text-main" style={{ width: `${Math.random() * 30 + 30}%` }}></div>
              <div className="skeleton-text-sub" style={{ width: `${Math.random() * 20 + 15}%` }}></div>
            </div>
          </div>
          <div className="skeleton-right">
            <div className="skeleton-action"></div>
            <div className="skeleton-action"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="vista-impositiva-container">
      <div className="vista-impositiva-header">
        {history.length > 0 && (
          <button className="btn-volver-impositivo" onClick={handleVolver} disabled={loading}>
            <i className="bi bi-arrow-left"></i> Volver ({n(history[history.length - 1].nombre)})
          </button>
        )}
        {carpetaActual ? (
          <h2><i className="bi bi-building" style={{ marginRight: 8, color: '#0f766e' }}></i> {titulo} - {n(carpetaActual.nombre)}</h2>
        ) : (
          <h2><i className="bi bi-building" style={{ marginRight: 8, color: '#0f766e' }}></i> {titulo}</h2>
        )}
        <div className="vista-sort-container">
          <i className="bi bi-sort-down" style={{ color: '#64748b', fontSize: '1.1rem' }}></i>
          <select
            className="vista-sort-select"
            value={sortOrder}
            onChange={handleSortChange}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            className={`btn-refrescar ${refreshing || loading ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Actualizar carpeta"
          >
            <i className={`bi ${refreshing || loading ? 'bi-arrow-clockwise' : 'bi-arrow-repeat'}`}></i>
          </button>
        </div>
      </div>

      {error && (
        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '10px', borderRadius: '6px', margin: '0 0 15px 0', fontSize: '14px' }}>
          <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="carpeta-detalle">
          <div className="carpeta-detalle-header">
            <h3 style={{ color: '#e2e8f0', width: '200px', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 1.5s infinite ease-in-out' }}> </h3>
            <p style={{ color: '#e2e8f0', width: '100px', height: '16px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 1.5s infinite ease-in-out', marginTop: '8px' }}> </p>
          </div>
          <div className="archivos-list">
            <h4>Documentacion</h4>
            <SkeletonLoader />
          </div>
        </div>
      ) : (!carpetaId) ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Esta entidad no tiene una carpeta asociada.
        </div>
      ) : !carpetaActual ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Directorio no encontrado.
        </div>
      ) : (
        <div className="carpeta-detalle">
          <div className="carpeta-detalle-header">
            <h3><i className="bi bi-folder-open" style={{ marginRight: 8, color: '#0f766e' }}></i> {n(carpetaActual.nombre)}</h3>
            <p>Directorio actual</p>
          </div>

          {puedeSubir && (
            <UploadZone onSubir={handleSubirArchivo} loading={loading} />
          )}

          <div className="archivos-list">
            <h4>Documentacion</h4>
            {sortedContent.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontStyle: 'italic' }}>
                No hay documentos en esta carpeta.
              </div>
            ) : (
              <ul className="lista-items">
                {sortedContent
                  .filter(item => isAdmin || !elementosOcultos.some(e => e.id === item.id))
                  .map((item) => {
                    const elementoOculto = elementosOcultos.find(e => e.id === item.id);
                    const estaOculto = !!elementoOculto;
                    return (
                  <li key={item.id} className={`archivo-item ${estaOculto ? 'item-oculto' : ''}`} style={{ cursor: 'pointer' }} onClick={() => handleItemClick(item)}>
                    <div className="archivo-info-izquierda">
                      <span className="file-type-icon">
                        {item.tipo === 'Carpeta' 
                          ? <i className="bi bi-folder" style={{ color: '#0f766e', fontSize: '1.5rem' }}></i>
                          : <i className={getFileIcon(item.nombre)} style={{ color: getFileIconColor(item.nombre), fontSize: '1.5rem' }}></i>
                        }
                      </span>
                      <div className="archivo-textos">
                        <div className="archivo-nombre" style={{ color: item.tipo === 'Carpeta' ? '#0f766e' : '#334155', fontWeight: item.tipo === 'Carpeta' ? '600' : '400' }}>
                          {item.tipo === 'Carpeta' && carpetasConNovedades && carpetasConNovedades.includes(item.id) && (
                            <span style={{ color: '#dc2626', marginRight: 6, fontSize: '10px', verticalAlign: 'super' }}>●</span>
                          )}
                          {n(item.nombre)}
                          {isAdmin && elementoOculto && (
                            <span style={{ color: '#b45309', marginLeft: '8px', fontSize: '0.8em' }}>
                              (oculto por {elementoOculto.ocultor} el {new Date(elementoOculto.fecha).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })})
                            </span>
                          )}
                        </div>
                        <div className="archivo-meta">
                          {item.tipo} {item.size ? `- ${item.size}` : ''} {item.fecha ? `- ${item.fecha}` : ''}
                        </div>
                      </div>
                    </div>
                    
                    <div className="archivo-acciones" onClick={(e) => e.stopPropagation()}>
                      {isAdmin && (
                        <button 
                          className="btn-accion btn-visibilidad" 
                          title={estaOculto ? "Mostrar al cliente" : "Ocultar al cliente"}
                          onClick={() => handleToggleVisibilidad(item)}
                        >
                          <i className={`bi ${estaOculto ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                      )}
                      
                      {item.tipo === 'Archivo' && (
                        <>
                          <button 
                            className="btn-accion btn-descargar" 
                            title="Descargar"
                            onClick={() => handleDescargarArchivo(item)}
                          >
                            <i className="bi bi-download"></i>
                          </button>
                          {isAdmin && (
                            <button 
                              className="btn-accion btn-notificar" 
                              title="Notificar al cliente"
                              onClick={() => handleNotificarCliente(item)}
                            >
                              <i className="bi bi-send"></i>
                            </button>
                          )}
                          {isAdmin && (
                            <button 
                              className="btn-accion btn-eliminar" 
                              title="Eliminar"
                              onClick={() => handleEliminarClick(item)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                    );
                  })}
              </ul>
            )}
          </div>
        </div>
      )}
      {archivoAEliminar && (
        <div className="modal-overlay" onClick={cancelarEliminacion}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Estas seguro?</h3>
            <p>Se borrara permanentemente "{archivoAEliminar.nombre}".</p>
            <div className="modal-actions">
              <button className="btn-cancelar" onClick={cancelarEliminacion}>Cancelar</button>
              <button className="btn-confirmar-eliminar" onClick={confirmarEliminacion}><i className="bi bi-trash"></i> Si, Eliminar</button>
            </div>
          </div>
        </div>
      )}

      <FilePreviewModal 
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fileUrl={previewUrl}
        fileName={previewFile ? previewFile.nombre : ''}
        onDownload={previewFile ? () => handleDescargarArchivo(previewFile) : null}
      />

    </div>
  );
};

export default VistaCarpetaGenerica;
